import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { SquaresPlusIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import useTabStore from "../stores/useTabStore";
import { trackFeatureUsage } from "../utils/gtm";
import TemplateGalleryModal from "@/onboarding/TemplateGalleryModal";

interface NewTabModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Replaces the old window.prompt() flow for creating a dashboard tab.
 * Creates a blank tab; starter-template selection is layered on by the
 * onboarding template gallery.
 */
export default function NewTabModal({ isOpen, onClose }: NewTabModalProps) {
  const [title, setTitle] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const addTab = useTabStore((state) => state.addTab);

  const handleCreate = () => {
    const trimmed = title.trim();
    if (!trimmed) return;
    addTab(trimmed);
    trackFeatureUsage({
      feature_name: "dashboard_tab",
      feature_action: "create",
      feature_detail: "blank",
    });
    setTitle("");
    onClose();
  };

  const handleClose = () => {
    setTitle("");
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="relative z-[100] no-drag"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/80 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150"
      />
      <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
        <DialogPanel
          transition
          className="w-full max-w-md transform overflow-hidden rounded-lg bg-gray-800 shadow-xl transition-all data-[closed]:translate-y-2 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[closed]:scale-95"
        >
          <div className="p-5">
            <div className="flex gap-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-squawk-blue/20">
                <SquaresPlusIcon className="size-5 text-squawk-blue" />
              </div>
              <div className="flex-1 min-w-0">
                <DialogTitle className="text-sm font-semibold text-gray-100">
                  New Tab
                </DialogTitle>
                <p className="mt-1.5 text-sm text-gray-400 leading-relaxed">
                  Name your new dashboard tab. You can add widgets to it once
                  it&apos;s created.
                </p>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreate();
                  }}
                  placeholder="e.g. Macro, Equities, Energy…"
                  maxLength={40}
                  autoFocus
                  className="mt-3 w-full rounded-md border border-gray-600 bg-gray-700/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-squawk-blue focus:border-transparent"
                />
              </div>
            </div>

            <button
              type="button"
              onClick={() => setGalleryOpen(true)}
              className="mt-4 flex w-full items-center gap-2 rounded-md border border-gray-700 bg-gray-900/50 px-3 py-2.5 text-sm text-gray-300 hover:border-squawk-blue/60 hover:text-gray-100 transition-colors"
            >
              <Squares2X2Icon className="h-4 w-4 text-squawk-blue" />
              Or start from a template…
            </button>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium rounded-md bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={!title.trim()}
                className="px-4 py-2 text-sm font-medium rounded-md bg-squawk-blue/20 border border-squawk-blue/50 text-squawk-blue hover:bg-squawk-blue/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Tab
              </button>
            </div>
          </div>
        </DialogPanel>
      </div>

      <TemplateGalleryModal
        isOpen={galleryOpen}
        onClose={() => {
          setGalleryOpen(false);
          handleClose();
        }}
      />
    </Dialog>
  );
}
